import { Limits } from "~/types/billing";
import { useAccount } from "./useAccount";
import { DISABLE_PAYMENTS, isFeatureEnabledOnPlan } from "~/helpers/plans";
import { PlanStatus } from "~/types/account";
import { triggerShowPastDueModal } from "~/helpers/customEvents";

export const usePlanFeature = () => {
  const { account } = useAccount();

  const isFeatureEnabled = (limitType: Limits) => {
    if (DISABLE_PAYMENTS) return true;

    if (account?.active_subscription?.paused_at) return false;

    return isFeatureEnabledOnPlan(
      limitType,
      account?.active_subscription?.plan
    );
  };

  const isFeatureDisabled = (value: Limits) => !isFeatureEnabled(value);

  const isFeaturePaused = () => !!account?.active_subscription?.paused_at;

  // PD users have no active_subscription — the past-due record sits on
  // last_subscription. Mirror the home page banner check, including the
  // stripe_card_updated_at freshness guard so we don't nag users who already
  // updated their card but whose subscription hasn't transitioned yet.
  const isPastDue = () => {
    if (DISABLE_PAYMENTS) return false;
    if (account?.last_subscription?.status !== PlanStatus.PAST_DUE)
      return false;

    if (account?.stripe_card_updated_at && account?.last_subscription) {
      const lastCardUpdateAt = new Date(account.stripe_card_updated_at);
      const subscriptionUpdatedAt = new Date(
        account.last_subscription.updated_at
      );
      if (lastCardUpdateAt >= subscriptionUpdatedAt) return false;
    }

    return true;
  };

  // Returns true when the user was blocked (modal shown) so callers can
  // short-circuit the premium action. Non-PD users get false and proceed normally.
  const guardPastDue = () => {
    if (isPastDue()) {
      triggerShowPastDueModal();
      return true;
    }
    return false;
  };

  return {
    isFeatureEnabled,
    isFeatureDisabled,
    isFeaturePaused,
    isPastDue,
    guardPastDue,
  };
};
